import { useImages, useViewerThumbnailUrl } from '@/services/image.service';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { ImageIcon } from 'lucide-react';
import { sampleRotation } from '@/lib/rotation';
import { type Crop, croppedSize, hasCrop, USUAL_SCAN_MM } from '@/lib/crop';

// The thumbnail is a quarter-turn of the portrait scan, so the frame is the
// portrait box laid on its side, as wide as its container: the image is the
// frame's height wide and its width tall (110/154 and 154/110 of it), centred,
// so the rotation fills the frame exactly whichever direction it turns.
// Centred by its offsets, (100% - size) / 2, not by a translate, which the
// rotation would turn along with it.
const FRAME = 'relative w-full aspect-[154/110]';
const IMAGE = 'absolute left-[14.2857%] top-[-20%] w-[71.4286%] h-[140%]';
// The portrait box's shape, width / height.
const IMAGE_RATIO = 110 / 154;

interface SampleImagePreviewProps {
    projectId: string;
    sampleId: string;
    /** Same rule as the viewer: which way the sample's images turn. */
    rotateClockwise?: boolean;
    /** The scanner's black frame, in millimetres, cropped off as in the viewer. */
    crop?: Crop | null;
}

export default function SampleImagePreview({
    projectId,
    sampleId,
    rotateClockwise,
    crop,
}: SampleImagePreviewProps) {
    const { t } = useTranslation();
    const { data: images, isLoading: imagesLoading } = useImages(
        projectId,
        sampleId,
    );

    const lastImage =
        images && images.length > 0 ? images[images.length - 1] : null;

    const {
        data: imageUrl,
        isLoading: imageUrlLoading,
        isError,
    } = useViewerThumbnailUrl(projectId, sampleId, lastImage?.id);

    if (imagesLoading || imageUrlLoading) {
        return (
            <div className={`${FRAME} rounded-md overflow-hidden bg-[#ecede8]`}>
                <Skeleton className="w-full h-full" />
            </div>
        );
    }

    if (isError || !lastImage || !imageUrl) {
        return (
            <div
                className={`${FRAME} rounded-md overflow-hidden bg-[#ecede8] border border-dashed border-[#dadbd5] flex flex-col items-center justify-center text-[#7a8077]`}
            >
                <ImageIcon className="h-4 w-4 mb-1" />
                <span className="text-[9px] uppercase tracking-wide">
                    {t('No image')}
                </span>
            </div>
        );
    }

    if (hasCrop(crop)) {
        // The kept part of the scan, covering the portrait box the way
        // object-cover would: a window with the kept region's shape, and the
        // whole thumbnail inside it scaled and shifted so only that region
        // shows. Percentages of the window keep the scaling uniform. The
        // thumbnail is assumed to be the usual scan size, as only its shape
        // matters here.
        const full = USUAL_SCAN_MM;
        const kept = croppedSize(full.x, full.y, crop);
        const keptRatio = kept.width / kept.height;
        return (
            <div
                className={`${FRAME} rounded-md overflow-hidden bg-[#ecede8] ring-1 ring-[#dadbd5]`}
            >
                <div
                    className={`${IMAGE} overflow-hidden flex items-center justify-center`}
                    style={{
                        transform: `rotate(${sampleRotation(rotateClockwise)}deg)`,
                    }}
                >
                    <div
                        className="relative flex-shrink-0 overflow-hidden"
                        style={{
                            aspectRatio: `${keptRatio}`,
                            ...(keptRatio < IMAGE_RATIO
                                ? { width: '100%' }
                                : { height: '100%' }),
                        }}
                    >
                        <img
                            src={imageUrl}
                            alt={t('Sample preview')}
                            className="absolute max-w-none"
                            style={{
                                width: `${(full.x / kept.width) * 100}%`,
                                height: `${(full.y / kept.height) * 100}%`,
                                left: `${(-crop.left / kept.width) * 100}%`,
                                top: `${(-crop.top / kept.height) * 100}%`,
                            }}
                            loading="lazy"
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`${FRAME} rounded-md overflow-hidden bg-[#ecede8] ring-1 ring-[#dadbd5]`}
        >
            <img
                src={imageUrl}
                alt={t('Sample preview')}
                className={`${IMAGE} max-w-none object-cover`}
                style={{
                    transform: `rotate(${sampleRotation(rotateClockwise)}deg)`,
                }}
                loading="lazy"
            />
        </div>
    );
}
